/**
 * Travel Agent Service - Client-side Firestore and Cloud Function integration
 *
 * Manages AI travel agent sessions and messages in Firestore.
 * Follows the effectiveUserId pattern for shared trip support.
 *
 * Firestore Structure:
 * users/{userId}/trips/{tripId}/agentSessions/{sessionId}
 *   - title, createdAt, lastActivity, status, messageCount
 *   messages/{messageId}
 *     - role, content, artifacts, timestamp
 *
 * effectiveUserId Pattern:
 * - All Firestore writes use effectiveUserId (trip owner for shared trips)
 * - Events use currentUser.uid
 * - See CLAUDE.md "Shared Trip Data Access Pattern"
 */

import { db } from '@trip-planner-monorepo/shared';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteField,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ARTIFACT_PATTERN = /\[\[ARTIFACT:([^:\]]+):([^:\]]+):([^\]]+)\]\]/g;
const AGENT_TIMEOUT_MS = 660000; // 11 minutes — matches CF timeoutSeconds: 540 with buffer

function extractArtifactsFromText(text = '') {
  const sourceText = String(text || '');
  if (!sourceText) return [];

  const artifacts = [];
  let match;
  while ((match = ARTIFACT_PATTERN.exec(sourceText)) !== null) {
    const [token, type, id, label] = match;
    const artifact = {
      type: String(type || '').trim(),
      id: String(id || '').trim(),
      label: String(label || '').trim(),
      token,
      start: match.index,
      end: match.index + token.length,
      source: 'inline_token',
      version: 1
    };
    if (artifact.type && artifact.id && artifact.label) {
      artifacts.push(artifact);
    }
  }

  const seen = new Set();
  return artifacts.filter((artifact) => {
    const key = `${artifact.type}|${artifact.id}|${artifact.label}|${artifact.start}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeArtifacts(artifacts = []) {
  if (!Array.isArray(artifacts)) return [];
  const normalized = artifacts
    .map((item) => {
      const normalized = {
        type: String(item?.type || '').trim(),
        id: String(item?.id || '').trim(),
        label: String(item?.label || '').trim(),
        token: item?.token ? String(item.token) : undefined,
        start: Number.isFinite(item?.start) ? item.start : undefined,
        end: Number.isFinite(item?.end) ? item.end : undefined,
        source: item?.source ? String(item.source) : undefined,
        version: Number.isFinite(item?.version) ? item.version : 1
      };
      // Preserve entity-level fields from Cloud Function response
      if (item?.entityType) normalized.entityType = String(item.entityType);
      if (item?.entity) normalized.entity = item.entity;
      if (item?.items) normalized.items = item.items;
      return normalized;
    })
    .filter((item) => item.type && item.id && item.label);

  const seen = new Set();
  return normalized.filter((artifact) => {
    const key = `${artifact.type}|${artifact.id}|${artifact.label}|${artifact.start ?? 'na'}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

class TravelAgentService {
  constructor() {
    this._initialized = false;
  }

  /**
   * Initialize the service (deferred init pattern — no-op for now)
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;
    console.log('[TravelAgentService] initialized');
  }

  // ---------------------------------------------------------------------------
  // Session Management
  // ---------------------------------------------------------------------------

  /**
   * Create a new agent session in Firestore
   *
   * @param {string} userId - effectiveUserId (trip owner for shared trips)
   * @param {string} tripId - Trip identifier
   * @param {string} [firstMessage] - Optional first message for context
   * @returns {Promise<string>} The new session ID
   */
  async createSession(userId, tripId, firstMessage = '') {
    try {
      if (!userId || !tripId) {
        throw new Error('userId and tripId are required');
      }

      const sessionsRef = collection(db, 'users', userId, 'trips', tripId, 'agentSessions');

      const sessionDoc = {
        title: firstMessage
          ? firstMessage.substring(0, 50) + (firstMessage.length > 50 ? '...' : '')
          : 'New conversation',
        createdAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        status: 'active',
        messageCount: 0
      };

      const docRef = await addDoc(sessionsRef, sessionDoc);
      console.log(`[TravelAgentService] Session created: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error('[TravelAgentService] Error creating session:', error);
      throw error;
    }
  }

  /**
   * Update session title (e.g. after LLM generates a title, or user edits inline)
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Session identifier
   * @param {string} title - New title
   * @returns {Promise<void>}
   */
  async updateSessionTitle(userId, tripId, sessionId, title) {
    try {
      if (!userId || !tripId || !sessionId) {
        throw new Error('userId, tripId, and sessionId are required');
      }

      const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
      await updateDoc(sessionRef, {
        title: title.trim() || 'Untitled',
        lastActivity: serverTimestamp()
      });
      console.log(`[TravelAgentService] Session title updated: ${sessionId}`);
    } catch (error) {
      console.error('[TravelAgentService] Error updating session title:', error);
      throw error;
    }
  }

  /**
   * Subscribe to sessions for a trip (real-time)
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {Function} callback - Called with sessions array on change
   * @returns {Function} Unsubscribe function
   */
  subscribeToSessions(userId, tripId, callback) {
    if (!userId || !tripId) {
      console.warn('[TravelAgentService] subscribeToSessions: missing userId or tripId');
      return () => {};
    }

    const sessionsRef = collection(db, 'users', userId, 'trips', tripId, 'agentSessions');
    const q = query(sessionsRef, orderBy('lastActivity', 'desc'), limit(50));

    return onSnapshot(
      q,
      (snapshot) => {
        const sessions = snapshot.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || null,
            lastActivity: docSnap.data().lastActivity?.toDate?.()?.toISOString() || null
          }))
          .filter((s) => s.status !== 'deleted');
        callback(sessions);
      },
      (error) => {
        console.error('[TravelAgentService] Sessions subscription error:', error);
        callback([]);
      }
    );
  }

  /**
   * Archive a session (soft delete)
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Session to archive
   * @returns {Promise<void>}
   */
  async archiveSession(userId, tripId, sessionId) {
    try {
      if (!userId || !tripId || !sessionId) {
        throw new Error('userId, tripId, and sessionId are required');
      }

      const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
      await updateDoc(sessionRef, {
        status: 'archived',
        lastActivity: serverTimestamp()
      });
      console.log(`[TravelAgentService] Session archived: ${sessionId}`);
    } catch (error) {
      console.error('[TravelAgentService] Error archiving session:', error);
      throw error;
    }
  }

  /**
   * Reset session status back to 'active' after the agent has responded.
   *
   * The Firestore delivery mechanism sets status to 'complete' when the CF finishes.
   * Without this reset, on the next page visit the auto-select filter (which only
   * looks for 'active' sessions) treats the conversation as gone and creates a new
   * empty session, making it appear as if the history was lost.
   *
   * Called by TravelAgentPage after `sendToAgent` resolves.
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Session identifier
   * @returns {Promise<void>}
   */
  async resetSessionStatus(userId, tripId, sessionId) {
    if (!userId || !tripId || !sessionId) return;
    const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
    await updateDoc(sessionRef, {
      status: 'active',
      pendingResult: deleteField(),
      pendingResultAt: deleteField(),
      lastActivity: serverTimestamp()
    }).catch((err) => {
      // Non-fatal — session will still be visible via the broadened auto-select filter
      console.warn('[TravelAgentService] resetSessionStatus failed (non-fatal):', err);
    });
  }

  /**
   * Restore an archived session back to active
   */
  async restoreSession(userId, tripId, sessionId) {
    try {
      if (!userId || !tripId || !sessionId) throw new Error('userId, tripId, and sessionId are required');
      const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
      await updateDoc(sessionRef, {
        status: 'active',
        lastActivity: serverTimestamp()
      });
      console.log(`[TravelAgentService] Session restored: ${sessionId}`);
    } catch (error) {
      console.error('[TravelAgentService] Error restoring session:', error);
      throw error;
    }
  }

  /**
   * Permanently delete a session (hard delete — sets status to 'deleted', filtered by subscribeToSessions)
   */
  async deleteSession(userId, tripId, sessionId) {
    try {
      if (!userId || !tripId || !sessionId) throw new Error('userId, tripId, and sessionId are required');
      const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
      await updateDoc(sessionRef, {
        status: 'deleted',
        lastActivity: serverTimestamp()
      });
      console.log(`[TravelAgentService] Session deleted: ${sessionId}`);
    } catch (error) {
      console.error('[TravelAgentService] Error deleting session:', error);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Message Management
  // ---------------------------------------------------------------------------

  /**
   * Save a message to Firestore under a session
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Session identifier
   * @param {'user'|'agent'} role - Message role
   * @param {string} content - Message content
   * @param {Object} [metadata] - Optional metadata (toolsUsed, etc.)
   * @returns {Promise<string>} The new message ID
   */
  async saveMessage(userId, tripId, sessionId, role, content, metadata = {}) {
    try {
      if (!userId || !tripId || !sessionId) {
        throw new Error('userId, tripId, and sessionId are required');
      }

      const messagesRef = collection(
        db,
        'users', userId,
        'trips', tripId,
        'agentSessions', sessionId,
        'messages'
      );

      const normalizedArtifacts = normalizeArtifacts(metadata?.artifacts);
      const artifacts = normalizedArtifacts.length > 0
        ? normalizedArtifacts
        : extractArtifactsFromText(content);

      const messageDoc = {
        role,
        content,
        artifacts,
        timestamp: serverTimestamp(),
        metadata
      };

      const docRef = await addDoc(messagesRef, messageDoc);

      // Update session lastActivity and messageCount
      const sessionRef = doc(db, 'users', userId, 'trips', tripId, 'agentSessions', sessionId);
      await updateDoc(sessionRef, {
        lastActivity: serverTimestamp()
      }).catch(() => {
        // Non-fatal if session update fails
      });

      console.log(`[TravelAgentService] Message saved: ${docRef.id} (role: ${role})`);
      return docRef.id;
    } catch (error) {
      console.error('[TravelAgentService] Error saving message:', error);
      throw error;
    }
  }

  /**
   * Subscribe to messages for a session (real-time)
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Session identifier
   * @param {Function} callback - Called with messages array on change
   * @returns {Function} Unsubscribe function
   */
  subscribeToMessages(userId, tripId, sessionId, callback) {
    if (!userId || !tripId || !sessionId) {
      console.warn('[TravelAgentService] subscribeToMessages: missing required params');
      return () => {};
    }

    const messagesRef = collection(
      db,
      'users', userId,
      'trips', tripId,
      'agentSessions', sessionId,
      'messages'
    );
    const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(200));

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          timestamp: docSnap.data().timestamp?.toDate?.()?.toISOString() || null
        }));
        callback(messages);
      },
      (error) => {
        console.error('[TravelAgentService] Messages subscription error:', error);
        callback([]);
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Artifact Entity Fetching
  // ---------------------------------------------------------------------------

  /**
   * Firestore collection name for a given entity type
   * @param {string} entityType - 'accommodation' | 'place' | 'travel' | 'day'
   * @returns {string|null}
   */
  _collectionForEntityType(entityType) {
    const map = {
      accommodation: 'accommodation',
      place: 'places',
      places: 'places',
      travel: 'travel',
      day: 'days',
      days: 'days'
    };
    return map[(entityType || '').toLowerCase()] || null;
  }

  /**
   * Fetch entity data from Firestore for an artifact chip.
   * Derives entity type from the artifact type string (e.g. ENTITY_VIEW_accommodation → accommodation).
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @param {Object} artifact - Artifact chip data { type, id, label, entityType? }
   * @returns {Promise<{entity: Object|null, entityType: string|null}>}
   */
  async fetchEntityForArtifact(userId, tripId, artifact) {
    try {
      if (!userId || !tripId || !artifact?.id) {
        return { entity: null, entityType: null };
      }

      // Derive entity type from artifact.entityType or parse from type string
      // e.g. ENTITY_VIEW_accommodation → accommodation
      let entityType = artifact.entityType || null;
      if (!entityType && artifact.type) {
        const match = artifact.type.match(/ENTITY_VIEW_(.+)/i);
        if (match) entityType = match[1].toLowerCase();
      }

      const collectionName = this._collectionForEntityType(entityType);
      if (!collectionName) {
        console.warn(`[TravelAgentService] Unknown entity type: ${entityType} for artifact ${artifact.id}`);
        return { entity: null, entityType };
      }

      const entityRef = doc(db, 'users', userId, 'trips', tripId, collectionName, artifact.id);
      const snap = await getDoc(entityRef);

      if (!snap.exists()) {
        console.warn(`[TravelAgentService] Entity not found: ${collectionName}/${artifact.id}`);
        return { entity: null, entityType };
      }

      return {
        entity: { id: snap.id, ...snap.data() },
        entityType
      };
    } catch (error) {
      console.error('[TravelAgentService] fetchEntityForArtifact error:', error);
      return { entity: null, entityType: artifact?.entityType || null };
    }
  }

  /**
   * Fetch days for a TRIP_TIMELINE (itinerary) artifact.
   * Returns days sorted by date ascending.
   *
   * @param {string} userId - effectiveUserId
   * @param {string} tripId - Trip identifier
   * @returns {Promise<Array<{id: string, name: string, date: string|null}>>}
   */
  async fetchTimelineForArtifact(userId, tripId) {
    try {
      if (!userId || !tripId) return [];

      const daysSnap = await getDocs(
        collection(db, 'users', userId, 'trips', tripId, 'days')
      );

      const days = [];
      daysSnap.forEach((docSnap) => {
        const d = docSnap.data();
        days.push({
          id: docSnap.id,
          name: d.name || '',
          date: d.date || null
        });
      });

      // Sort by date ascending (nulls last); date is stored as ISO string e.g. "2026-03-15"
      days.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.localeCompare(b.date);
      });

      return days;
    } catch (error) {
      console.error('[TravelAgentService] fetchTimelineForArtifact error:', error);
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Cloud Function Call
  // ---------------------------------------------------------------------------

  /**
   * Send a message to the travelAgent Cloud Function.
   *
   * Result is delivered via Firestore onSnapshot rather than the HTTP response body.
   * This avoids deadline-exceeded errors caused by idle TCP connections being killed
   * by routers/CDN edges during long Gemini generation runs (60-90 s of silence).
   *
   * Flow:
   *  1. Open Firestore onSnapshot listener on the session doc BEFORE calling the CF.
   *  2. Fire the CF via httpsCallable (do not await its resolved value).
   *  3. CF writes status:'processing' early, then status:'complete' + pendingResult when done.
   *  4. onSnapshot fires → promise resolves with the result.
   *  5. A 10-minute timeout guards against CF crashes that never write status:'error'.
   *
   * @param {string} ownerId - Trip owner's user ID (effectiveUserId for shared trips)
   * @param {string} tripId - Trip identifier
   * @param {string} sessionId - Current session ID
   * @param {string} userMessage - The user's message
   * @param {Array} conversationHistory - Previous messages in Gemini format
   * @param {boolean} [generateTitle=false] - Whether to auto-generate a session title
   * @param {boolean} [debug=false] - Include debugTrace payload for browser diagnostics
   * @returns {Promise<{text: string, title?: string, toolsUsed: string[], artifacts: object[], error?: string, debugTrace?: object}>}
   */
  sendToAgent(ownerId, tripId, sessionId, userMessage, conversationHistory = [], generateTitle = false, debug = false) {
    const RESULT_TIMEOUT_MS = 600000; // 10 minutes — CF limit is 9 min (540 s)
    // Only accept pendingResult written within the last 30 s. Guards against the initial
    // onSnapshot firing with a stale 'complete' doc from the previous message's run.
    const RESULT_FRESHNESS_MS = 30000;

    return new Promise((resolve) => {
      let unsubscribe = null;
      let timeoutId = null;
      let resolved = false; // guard against the listener firing after cleanup

      const cleanup = () => {
        if (unsubscribe) { unsubscribe(); unsubscribe = null; }
        if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
      };

      const resolveWithResult = (pendingResult = {}) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        const normalizedArtifacts = normalizeArtifacts(pendingResult.artifacts);
        const extractedArtifacts = normalizedArtifacts.length > 0
          ? normalizedArtifacts
          : extractArtifactsFromText(pendingResult.text || '');
        resolve({ ...pendingResult, artifacts: extractedArtifacts });
      };

      const resolveWithError = (errorMessage) => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve({
          text: 'I apologize, but I encountered an error. Please try again.',
          toolsUsed: [],
          artifacts: [],
          error: errorMessage,
        });
      };

      // Open the listener BEFORE calling the CF so we never miss a fast completion.
      const sessionDocRef = doc(db, 'users', ownerId, 'trips', tripId, 'agentSessions', sessionId);
      unsubscribe = onSnapshot(
        sessionDocRef,
        (snapshot) => {
          const data = snapshot.data();
          if (!data) return;

          // Freshness guard — ignore any pendingResult written before this call started.
          // The CF writes pendingResultAt as a server timestamp; skip stale docs so the
          // initial onSnapshot (which fires with the previous run's 'complete' state) does
          // not resolve this call with the wrong message's result.
          const resultAge = data.pendingResultAt?.toMillis
            ? Date.now() - data.pendingResultAt.toMillis()
            : Infinity;
          if (resultAge > RESULT_FRESHNESS_MS) return;

          if (data.status === 'complete' && data.pendingResult) {
            resolveWithResult(data.pendingResult);
          } else if (data.status === 'error' && data.pendingResult) {
            resolveWithError(data.pendingResult.error || 'Unknown error');
          }
        },
        (snapshotError) => {
          console.error('[TravelAgentService] Firestore result listener error:', snapshotError);
          resolveWithError(snapshotError.message);
        }
      );

      // 10-minute safety net — fires if CF crashes before writing status:'error'.
      timeoutId = setTimeout(() => {
        console.error('[TravelAgentService] Firestore result listener timed out');
        resolveWithError('The request timed out. Please try again.');
      }, RESULT_TIMEOUT_MS);

      // Fire the CF. Result arrives via Firestore, not the HTTP response.
      // If the HTTP call itself fails (network reset, etc.) the CF may still have
      // completed and written to Firestore, so we do NOT resolve on HTTP error —
      // the onSnapshot listener will deliver the result, or the timeout will clean up.
      const functions = getFunctions();
      const callTravelAgent = httpsCallable(functions, 'travelAgent', { timeout: AGENT_TIMEOUT_MS });
      callTravelAgent({ ownerId, tripId, sessionId, userMessage, conversationHistory, generateTitle, debug })
        .catch((error) => {
          console.error('[TravelAgentService] Cloud Function HTTP call failed:', error);
          // Do not resolve here — Firestore delivery or timeout will handle it.
        });
    });
  }
}

export const travelAgentService = new TravelAgentService();
export default travelAgentService;
